// =============================================
// Admin Dashboard Page
// Kipikid - מערכת נוכחות ילדים בגנים
// חיבור ל-API אמיתי
// =============================================

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, Building2, Baby, Landmark, TrendingUp } from 'lucide-react';
import { adminApi, type AdminDashboardStats } from '../../services/api';
import { adminColors as colors } from '../../styles/colors';
import { useOrgFilter } from '../../hooks/useOrgFilter';

export default function AdminDashboard() {
  const { orgFilter, isSysAdmin, user } = useOrgFilter();
  const [stats, setStats] = useState<AdminDashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadDashboard = async () => {
      try {
        const result = await adminApi.getDashboard(orgFilter);
        if (result.success && result.data) {
          setStats(result.data);
        }
      } catch (err) {
        if (import.meta.env.DEV) console.error('Failed to load dashboard:', err);
      } finally {
        setLoading(false);
      }
    };
    loadDashboard();
  }, [orgFilter]);
  const statCards = stats ? [
    // הצגת ארגונים רק למנהל מערכת
    ...(isSysAdmin ? [{ label: 'ארגונים', value: stats.totalOrganizations.toString(), icon: Landmark, color: colors.warning, bgColor: colors.warningLight }] : []),
    { label: 'גנים', value: stats.totalKindergartens.toString(), icon: Building2, color: colors.primary, bgColor: colors.primaryLight },
    { label: 'עובדים', value: stats.totalStaff.toString(), icon: Users, color: colors.secondary, bgColor: colors.secondaryLight },
    { label: 'ילדים רשומים', value: stats.totalChildren.toString(), icon: Baby, color: colors.success, bgColor: colors.successLight },
  ] : [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Header */}
      <div>
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: colors.text, marginBottom: '4px' }}>
          לוח בקרה
        </h1>
        <p style={{ fontSize: '14px', color: colors.textLight }}>
          {user?.organizationName ? `${user.organizationName}` : 'ברוך הבא למערכת הניהול של KipiKid'}
        </p>
      </div>

      {/* Stats Grid */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: colors.textLight }}>
          טוען נתונים...
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: '16px',
        }}>
          {statCards.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              style={{
                backgroundColor: 'white',
                borderRadius: '16px',
                padding: '20px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              }}
            >
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                backgroundColor: stat.bgColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '12px',
              }}>
                <stat.icon size={24} color={stat.color} />
              </div>
              <p style={{ fontSize: '28px', fontWeight: 'bold', color: colors.text, marginBottom: '4px' }}>
                {stat.value}
              </p>
              <p style={{ fontSize: '13px', color: colors.textLight }}>
                {stat.label}
              </p>
            </motion.div>
          ))}
        </div>
      )}

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        }}
      >
        <h2 style={{ fontSize: '16px', fontWeight: '600', color: colors.text, marginBottom: '16px' }}>
          פעולות מהירות
        </h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <QuickActionButton
            icon={Users}
            label="ניהול עובדים"
            href="/admin/staff"
            color={colors.primary}
            bgColor={colors.primaryLight}
          />
          <QuickActionButton
            icon={Building2}
            label="ניהול גנים"
            href="/admin/kindergartens"
            color={colors.success}
            bgColor={colors.successLight}
          />
        </div>
      </motion.div>
    </div>
  );
}

function QuickActionButton({
  icon: Icon,
  label,
  href,
  color,
  bgColor,
}: {
  icon: React.ElementType;
  label: string;
  href: string;
  color: string;
  bgColor: string;
}) {
  return (
    <motion.a
      href={href}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px 16px',
        backgroundColor: bgColor,
        borderRadius: '10px',
        textDecoration: 'none',
        color: color,
      }}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      <Icon size={20} />
      <span style={{ fontSize: '14px', fontWeight: '500' }}>{label}</span>
    </motion.a>
  );
}
